import type { FilterSelectionMode } from '@api';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import styles from '@terminal/components/filter/Filter.module.scss';
import type { MultiselectIncludeExcludeOptionsProps } from '@terminal/components/filter/include-exclude/multiselect/MultiselectIncludeExcludeOptions.types';
import {
  getFilterOptionsBySelectionMode,
  getIncludeExcludeButtonLabels,
  INCLUDE_EXCLUDE_SELECTION_MODES,
} from '@terminal/components/filter/include-exclude/utils/include-exclude.utils';
import { MultiSelectedOptions } from '@terminal/components/filter/MultiSelectedOptions';
import { Option } from '@terminal/components/filter/Option';
import type { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import type { FilterGroupKeys } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useMemo, useState } from 'react';

import { updateMultiselectIncludeExcludeSelection } from './utils/multiselect-include-exclude.utils';

export const MultiselectIncludeExcludeOptions = <T extends FilterGroupKeys>({
  addFilterOption,
  defaultOption,
  disabledOptions,
  disabledTooltipLabel,
  filterId,
  filterKey,
  filterOptionGroups,
  isSearching,
  labels,
  minimumSelection,
  noResultsLabel,
  options,
  removeFilterOption,
  renderCustomFilterOptionLabel,
  selectedLabel,
  showEmpty,
}: MultiselectIncludeExcludeOptionsProps<T>) => {
  const { includedOptions, excludedOptions } =
    getFilterOptionsBySelectionMode(filterOptionGroups);
  const [selectionMode, setSelectionMode] = useState<FilterSelectionMode>(
    !includedOptions.length && excludedOptions.length ? 'exclude' : 'include'
  );
  const selectedOptions =
    selectionMode === 'include' ? includedOptions : excludedOptions;
  const disabledOptionKeys = useMemo(
    () => new Set(disabledOptions?.map(option => option.key)),
    [disabledOptions]
  );
  const selectedOptionKeys = useMemo(
    () => new Set(selectedOptions.map(option => option.key)),
    [selectedOptions]
  );
  const availableOptions = isSearching
    ? options
    : options.filter(option => !selectedOptionKeys.has(option.key));
  const showSelectedOptions = !isSearching && selectedOptions.length > 0;
  const totalSelectionCount = includedOptions.length + excludedOptions.length;
  const buttonLabels = getIncludeExcludeButtonLabels(
    labels,
    includedOptions,
    excludedOptions
  );

  const handleOptionSelect = (option: FilterOption, checked: boolean) => {
    updateMultiselectIncludeExcludeSelection({
      addFilterOption,
      checked,
      defaultOption,
      filterId,
      filterKey,
      filterOptionGroups,
      minimumSelection,
      option,
      removeFilterOption,
      selectionMode,
    });
  };

  return (
    <>
      <div className={styles.includeExcludeFilterMode}>
        <ButtonGroup
          labels={buttonLabels}
          activeIndex={INCLUDE_EXCLUDE_SELECTION_MODES.indexOf(selectionMode)}
          onClick={index =>
            setSelectionMode(
              INCLUDE_EXCLUDE_SELECTION_MODES[index] ?? 'include'
            )
          }
        />
      </div>
      {!showSelectedOptions && <div className={styles.separator} />}
      <div className={styles.scrollableList}>
        {showSelectedOptions && (
          <MultiSelectedOptions
            defaultOptionKey={
              totalSelectionCount === 1 ? defaultOption?.key : undefined
            }
            disabledTooltipLabel={disabledTooltipLabel}
            filterKey={filterKey}
            onFilterSelect={({ option }) => handleOptionSelect(option, true)}
            options={selectedOptions}
            renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
            title={selectedLabel}
          />
        )}
        {showSelectedOptions && availableOptions.length > 0 && (
          <div className={styles.separator} />
        )}
        {showEmpty ? (
          <p className={styles.noResults}>{noResultsLabel}</p>
        ) : (
          availableOptions.map(option => {
            const checked = selectedOptionKeys.has(option.key);
            const isDefaultOption =
              checked &&
              Boolean(minimumSelection) &&
              includedOptions.length + excludedOptions.length === 1 &&
              option.key === defaultOption?.key;
            const disabled = disabledOptionKeys.has(option.key);

            return (
              <Option
                key={option.key}
                multiselect
                option={option}
                disabled={disabled}
                isChecked={checked && !disabled}
                handleClick={() => handleOptionSelect(option, checked)}
                disabledTooltipLabel={
                  disabled || isDefaultOption ? disabledTooltipLabel : undefined
                }
                renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
              />
            );
          })
        )}
      </div>
    </>
  );
};
